// ============================================================
// DockerVolumes.jsx
// ============================================================
// Junior-dev Notes:
// - Concept: Containers are ephemeral (temporary). Volumes are persistent.
// - Strategy: Visualize the connection between Host FS and Container FS.
// - Branding: prodevconsult.com
// ============================================================

import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Button,
    Stack,
    styled,
    Divider,
    Paper,
    Chip,
} from "@mui/material";
import { Link } from "react-router-dom";

/* MUI Icons */
import HardDriveIcon from "@mui/icons-material/Storage";
import SettingsInputComponentIcon from "@mui/icons-material/SettingsInputComponent";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import SecurityIcon from "@mui/icons-material/Security";
import FolderIcon from "@mui/icons-material/Folder";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

/* ── Brand tokens ────────────────────────────────────────── */
const DOCKER_BLUE = "#0DB7ED";
const NAVY_BG = "#001E2B";
const TEXT_WHITE = "#FFFFFF";

const BRAND = {
    muted: "rgba(255,255,255,0.6)",
    surface: "rgba(255,255,255,0.03)",
    border: "rgba(13, 183, 237, 0.2)",
    domain: "prodevconsult.com"
};

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: NAVY_BG,
    backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`,
    backgroundSize: "40px 40px",
    color: TEXT_WHITE,
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(10),
}));

const PersistenceBox = styled(Paper)(({ theme, side }) => ({
    padding: theme.spacing(4),
    backgroundColor: "rgba(255,255,255,0.02)",
    border: `1px solid ${BRAND.border}`,
    borderRadius: "16px",
    textAlign: "center",
    position: "relative",
    height: "200px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center"
}));

const BridgeLine = styled(Box)(({ theme }) => ({
    height: "2px",
    backgroundColor: DOCKER_BLUE,
    width: "100%",
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "&::before": {
        content: '""',
        width: "12px",
        height: "12px",
        backgroundColor: DOCKER_BLUE,
        borderRadius: "50%",
        position: "absolute",
        left: 0
    },
    "&::after": {
        content: '""',
        width: "12px",
        height: "12px",
        backgroundColor: DOCKER_BLUE,
        borderRadius: "50%",
        position: "absolute",
        right: 0
    }
}));

export default function DockerVolumes() {
    return (
        <PageWrapper>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 8, textAlign: { xs: "center", md: "left" } }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, justifyContent: { xs: "center", md: "flex-start" } }}>
                        <Chip
                            label="Data Management · Module 07"
                            sx={{ bgcolor: "rgba(13, 183, 237, 0.1)", color: DOCKER_BLUE, fontWeight: 700 }}
                        />
                        <Typography variant="caption" sx={{ color: DOCKER_BLUE, fontWeight: 800, letterSpacing: 1 }}>
                            {BRAND.domain.toUpperCase()}
                        </Typography>
                    </Stack>

                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Volumes & <span style={{ color: DOCKER_BLUE }}>Persistence</span>
                    </Typography>
                    <Typography variant="h6" sx={{ color: BRAND.muted, maxWidth: 800, lineHeight: 1.6 }}>
                        By default, data inside a container is deleted when the container stops.
                        Volumes allow you to "mount" folders from your computer into the container.
                    </Typography>
                </Box>

                {/* 2. THE VISUAL BRIDGE */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 12 }}>
                    <Grid item xs={12} md={5}>
                        <PersistenceBox>
                            <HardDriveIcon sx={{ fontSize: 48, color: DOCKER_BLUE, mb: 2 }} />
                            <Typography variant="h6" fontWeight={800}>Host Machine</Typography>
                            <Typography variant="caption" sx={{ color: BRAND.muted }}>/var/lib/docker/volumes</Typography>
                        </PersistenceBox>
                    </Grid>

                    <Grid item xs={12} md={2} sx={{ textAlign: 'center' }}>
                        <Stack alignItems="center" spacing={1}>
                            <SyncAltIcon sx={{ color: DOCKER_BLUE, fontSize: 32 }} />
                            <Typography variant="overline" sx={{ color: DOCKER_BLUE, fontWeight: 900 }}>MOUNT</Typography>
                        </Stack>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <PersistenceBox sx={{ borderStyle: 'dashed' }}>
                            <Box sx={{ p: 1, bgcolor: "rgba(0, 237, 100, 0.1)", borderRadius: '50%', mb: 2 }}>
                                <FolderIcon sx={{ fontSize: 40, color: "#00ED64" }} />
                            </Box>
                            <Typography variant="h6" fontWeight={800}>Container</Typography>
                            <Typography variant="caption" sx={{ color: BRAND.muted }}>/app/data</Typography>
                        </PersistenceBox>
                    </Grid>
                </Grid>



                {/* 3. TYPES OF PERSISTENCE */}
                <Grid container spacing={4} sx={{ mb: 10 }}>
                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 4, bgcolor: "rgba(255,255,255,0.02)", borderRadius: 4, height: '100%', border: `1px solid ${BRAND.border}` }}>
                            <Typography variant="h5" fontWeight={800} gutterBottom sx={{ color: DOCKER_BLUE }}>Named Volumes</Typography>
                            <Typography variant="body2" sx={{ color: BRAND.muted, mb: 3, lineHeight: 1.7 }}>
                                Managed entirely by Docker. You don't need to know where they are on your disk.
                                Great for databases like Postgres or MongoDB.
                            </Typography>
                            <Box sx={{ p: 2, bgcolor: "#011627", borderRadius: 2, border: "1px solid rgba(173, 219, 103, 0.2)" }}>
                                <Typography sx={{ fontFamily: 'monospace', color: "#ADDB67", fontSize: '0.85rem' }}>
                                    docker run -v my_data:/app/data
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 4, bgcolor: "rgba(255,255,255,0.02)", borderRadius: 4, height: '100%', border: `1px solid ${BRAND.border}` }}>
                            <Typography variant="h5" fontWeight={800} gutterBottom sx={{ color: DOCKER_BLUE }}>Bind Mounts</Typography>
                            <Typography variant="body2" sx={{ color: BRAND.muted, mb: 3, lineHeight: 1.7 }}>
                                You map a specific folder on your computer to a folder in the container.
                                Perfect for development so your code changes update instantly (Hot Reload).
                            </Typography>
                            <Box sx={{ p: 2, bgcolor: "#011627", borderRadius: 2, border: "1px solid rgba(173, 219, 103, 0.2)" }}>
                                <Typography sx={{ fontFamily: 'monospace', color: "#ADDB67", fontSize: '0.85rem' }}>
                                    docker run -v $(pwd):/app
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                {/* 4. WHY USE VOLUMES? */}
                <Box sx={{ mb: 10 }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 6, textAlign: "center" }}>
                        The <span style={{ color: DOCKER_BLUE }}>Persistence</span> Advantage
                    </Typography>
                    <Grid container spacing={3}>
                        {[
                            {
                                title: "Data Safety",
                                icon: <SecurityIcon />,
                                text: "Upgrading a container (deleting the old and starting a new one) won't lose your user's data."
                            },
                            {
                                title: "Performance",
                                icon: <SettingsInputComponentIcon />,
                                text: "Volumes are stored outside the 'Writable Layer' of the container, making I/O operations much faster."
                            },
                            {
                                title: "Sharing",
                                icon: <SyncAltIcon />,
                                text: "Multiple containers can mount the same volume to share data in real-time."
                            }
                        ].map((benefit, i) => (
                            <Grid item xs={12} md={4} key={i}>
                                <Stack alignItems="center" sx={{ textAlign: 'center', p: 2 }}>
                                    <Box sx={{ color: DOCKER_BLUE, mb: 2 }}>{benefit.icon}</Box>
                                    <Typography variant="h6" fontWeight={800} gutterBottom>{benefit.title}</Typography>
                                    <Typography variant="body2" sx={{ color: BRAND.muted, lineHeight: 1.6 }}>
                                        {benefit.text}
                                    </Typography>
                                </Stack>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ bgcolor: "rgba(255,255,255,0.05)", my: 8 }} />

                {/* 5. FOOTER */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="h5" sx={{ mb: 4, fontWeight: 700 }}>Now, let's link containers with Networking.</Typography>
                    <Link to="/docker-networking" style={{ textDecoration: "none" }}>
                        <Button
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                bgcolor: DOCKER_BLUE,
                                color: NAVY_BG,
                                fontWeight: 800,
                                px: 8,
                                py: 2,
                                borderRadius: "4px",
                                boxShadow: `0 4px 14px rgba(13, 183, 237, 0.3)`,
                                "&:hover": { bgcolor: "#0091ca" }
                            }}
                        >
                            Next: Docker Networking
                        </Button>
                    </Link>
                    <Typography variant="caption" display="block" sx={{ mt: 4, opacity: 0.3 }}>
                        Authorized Content by {BRAND.domain}
                    </Typography>
                </Box>

            </Container>
        </PageWrapper>
    );
}
